import { useEffect } from 'react';

import { isSameDay } from 'date-fns';
import { formatToISODate, getUtcTime, lodash } from 'helpers';
import { useObjectState } from 'hooks';
import { Calendar } from 'libs/ui';
import { Colors } from 'libs/ui/theme';

import { useFormContext } from '../../forms/productionCalendarForm';
import { useProductionCalendarContext } from '../../providers';

export interface ProductionCalendarProps {}

const stateColors = {
  add: {
    default: Colors.red,
    hover: Colors.red700,
  },
  remove: {
    default: Colors.black,
    hover: Colors.blue600,
  },
  initial: {
    default: Colors.red,
    hover: Colors.red700,
  },
};

export const ProductionCalendar = () => {
  const { initialValues, values, setValues, readOnly } = useFormContext();

  const { currentDate } = useProductionCalendarContext();

  const [datesState, setdDatesState, updateDatesState] = useObjectState<
    Record<string, 'add' | 'remove' | 'initial' | undefined>
  >({});

  const coloredDates = lodash.compact(
    Object.entries(datesState).map(([date, state]) =>
      state
        ? {
            date: getUtcTime(new Date(date)),
            color: stateColors[state],
          }
        : undefined
    )
  );

  const onClickDay = (day: Date | null) => {
    if (!day) return;
    const dayKey = formatToISODate(day);
    const dateState = datesState[dayKey];

    if (values.weekendsDate?.find((d) => isSameDay(d, day))) {
      setValues((v) => ({
        weekendsDate: v.weekendsDate?.filter((d) => !isSameDay(d, day)),
      }));
    } else {
      setValues((v) => ({
        weekendsDate: [...(v.weekendsDate || []), day],
      }));
    }

    if (dateState) {
      if (dateState === 'initial') {
        setdDatesState({ [dayKey]: 'remove' });
      } else if (dateState === 'remove') {
        setdDatesState({ [dayKey]: 'initial' });
      } else if (dateState === 'add') {
        setdDatesState({ [dayKey]: undefined });
      }
    } else {
      setdDatesState({ [dayKey]: 'add' });
    }
  };

  useEffect(() => {
    updateDatesState(
      initialValues?.weekendsDate?.reduce(
        (acc, date) => ({
          ...acc,
          [formatToISODate(date)]: 'initial',
        }),
        {}
      ) || {}
    );
  }, [updateDatesState, initialValues?.weekendsDate, readOnly]);

  return (
    <Calendar
      coloredDates={coloredDates}
      currentDate={currentDate}
      disablePast={!readOnly}
      readOnly={readOnly}
      onClickDay={onClickDay}
    />
  );
};
